import React, {useEffect, useState} from 'react';
import {CenterSpinner} from '@soap/baseweb'
import {useLazyQuery, useEvent, useLazyCommand, getHeader, headerKeys} from '@soap/modules'
import {HeadingSmall} from "baseui/typography";
import {useStyletron} from "baseui";
import {SpaceTopAndBottom} from "../../../z40-uicomp/layout";
import {KIND, Notification} from 'baseui/notification';
import {EditBillingDetails} from "../../../z50-domaincomp/drawers/edit-billing-details";
import {EditBillingCurrency} from "../../../z50-domaincomp/drawers/edit-billing-currency";
import {AggregateView, EntityMenu, SecondaryActionMenuButton, PrimaryActionMenuButton} from '@soap/baseweb';
import {ProgressBar} from "baseui/progress-bar";
import {ParagraphSmall} from "baseui/typography";
import {Card, CardHeader, CardTitle, CardContent} from "../../ui/card";
import {Button} from "../../ui/button";
import {ArrowLeft} from "lucide-react";
import {InvoicesCard} from "./InvoicesCard";
import {InfoTooltip} from "../InfoTooltip.jsx";
import {Input} from "../../ui/input";
import {useUserRole} from "../../../hooks/useUserRole";
import {appendTrialPromoToBeginOrder} from "../../../constants/trialPromoSession";
import {useFormatDisplayDate} from "../../../utils/useFormatDisplayDate";
import {useEnsurePaddleSetup} from "../../../hooks/useEnsurePaddleSetup";

export function Billing() {

    useEnsurePaddleSetup();
    const {requestingUserIsOrgAdmin} = useUserRole();
    const [getProfile, e138GotProfile] = useLazyQuery("C166v1_GetUserProfile", {});
    const [getSubscriptions, gotSubscriptions] = useLazyQuery("C170v1_GetSubscriptionPlanDetails", {
        C170_CountryCode: window.LYW.PaddleCountryCode
    });
    
    useEvent({
        eventName: "E145v1_BillingDetailsUpdated",
        onEventReceived: (e, v) => {
            getProfile();
        }
    }, [getProfile]);

    useEvent({
        eventName: "E188v1_BillingCurrencyUpdated",
        onEventReceived: (e, v) => {
            getProfile();
        }
    }, [getProfile]);

    // Fetch subscription details when profile is loaded
    useEffect(() => {
        if (e138GotProfile && !gotSubscriptions) {
            getSubscriptions();
        }
    }, [e138GotProfile, gotSubscriptions, getSubscriptions]);

    //* styling 
    const [css, theme] = useStyletron();
    const formatDate = useFormatDisplayDate();

    if (e138GotProfile) {

        const profileView = {
            BillingCountry: e138GotProfile.e138_BillingCountry,
            BillingCurrency: e138GotProfile.e138_BillingCurrency,
            BillingPostcode: e138GotProfile.e138_BillingPostcode,
            BillingTaxMode: e138GotProfile.e138_BillingTaxMode,
            UpdateUrl: e138GotProfile.e138_UpdatePaymentDetailsTransactionId,
            IsPastDue: e138GotProfile.e138_AccountPastDue,
            Invoices: e138GotProfile.e138_Invoices,
            IsOnPaidPlan: e138GotProfile.e138_QueuedPlan.e138_PlanKey2 !== 'free',
            QueuedPlan: e138GotProfile.e138_QueuedPlan,
            CurrentPlan: e138GotProfile.e138_ActivePlan,
        };

        const pastDue = profileView.IsPastDue;
        const hasBillingCountry = !!e138GotProfile.e138_BillingCountry;
        const hasBillingPostcode = !!e138GotProfile.e138_BillingPostcode;

        return (
            <div className="space-y-4">
                {pastDue ? <Notification kind={KIND.negative} overrides={{
                    Body: {style: {width: 'auto'}},
                }}> {profileView.IsOnPaidPlan ?
                    "Your account is Past Due. We we're not able to charge your registered Payment Method. Please update your billing details." :
                    "There was a problem renewing your account credit. Please raise a support ticket in order to resolve this issue."}
                </Notification> : null}

                {/* Billing Details Card */}
                <SpaceTopAndBottom>
                    
                            <div className="space-y-6">
                                {/* Billing Information */}
                                {profileView.IsOnPaidPlan && (hasBillingCountry || hasBillingPostcode) && (
                                    <div className="space-y-3">
                                        {hasBillingCountry && (
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Billing Country</span>
                                                <span className="text-gray-900">{profileView.BillingCountry}</span>
                                            </div>
                                        )}
                                        {hasBillingPostcode && (
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Billing Post/Zip Code</span>
                                                <span className="text-gray-900">{profileView.BillingPostcode}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Plan Information - Flippable Card */}
                                <div className="space-y-4">
                                    <PlanViewWithFlip profileView={profileView} e138GotProfile={e138GotProfile} gotSubscriptions={gotSubscriptions} requestingUserIsOrgAdmin={requestingUserIsOrgAdmin}/>
                                </div>
                            </div>
                        
                </SpaceTopAndBottom>
            </div>
        );
    } else {
        return <CenterSpinner/>;
    }
}

function PlanViewWithFlip({profileView, e138GotProfile, gotSubscriptions, requestingUserIsOrgAdmin}) {
    const [isFlipped, setIsFlipped] = useState(false);
    const [css, theme] = useStyletron();
    const [beginOrderTrigger] = useLazyCommand('C167v1_BeginOrder');
    
    // Student count state for family/tutor plans
    // Only show for paid plans (not free, not trial - both converted and unconverted)
    const planKey = profileView.QueuedPlan.e138_PlanKey2;
    const hasTrialKey = profileView.QueuedPlan.e138_TrialKey != null; // Both converted and unconverted trials have TrialKey
    const isOnPaidPlanNotTrial = profileView.IsOnPaidPlan && !hasTrialKey; // Must be paid plan AND not on any trial
    const isFamilyPlan = planKey === 'family' && isOnPaidPlanNotTrial;
    const isTutorPlan = planKey === 'tutor' && isOnPaidPlanNotTrial;
    const currentStudentCount = profileView.QueuedPlan.e138_Credit2?.e138_TotalStudents;
    
    const [familyStudents, setFamilyStudents] = useState(() => {
        if (isFamilyPlan && currentStudentCount) {
            return currentStudentCount;
        }
        return 1;
    });
    
    const [tutorStudents, setTutorStudents] = useState(() => {
        if (isTutorPlan && currentStudentCount) {
            return currentStudentCount;
        }
        return 10;
    });
    
    // Initialize student counts when profile loads
    useEffect(() => {
        if (isFamilyPlan && currentStudentCount) {
            setFamilyStudents(currentStudentCount);
        } else if (isTutorPlan && currentStudentCount) {
            setTutorStudents(currentStudentCount);
        }
    }, [isFamilyPlan, isTutorPlan, currentStudentCount]);
    
    // Check if student count differs from current plan
    const studentCountDiffers = () => {
        if (isFamilyPlan) {
            return familyStudents !== currentStudentCount;
        }
        if (isTutorPlan) {
            return tutorStudents !== currentStudentCount;
        }
        return false;
    };
    
    const handleUpgrade = () => {
        const country = gotSubscriptions?.e141_Pricing?.e141_BillingCountry || window.LYW.PaddleCountryCode;
        const currency = gotSubscriptions?.e141_Pricing?.e141_BillingCurrency || profileView.BillingCurrency || 'GBP';
        
        const c167 = {
            C167_PlanKey: planKey,
            C167_Country: country,
            C167_ChosenCurrency: currency
        };
        
        if (planKey === "family") {
            c167.C167_FamilyStudents = familyStudents;
        } else if (planKey === "tutor") {
            c167.C167_TutorStudents = tutorStudents;
        }
        
        beginOrderTrigger(appendTrialPromoToBeginOrder(c167));
    };
    
    useEvent({
        eventName: "E139v1_OrderProcessStarted",
        onEventReceived: (event) => {
            const orderPageUrl = window.location.origin + '/#/order/' + window.btoa(getHeader(event, headerKeys.statefulProcessId));
            window.location.assign(orderPageUrl);
        }
    });

    const rootActions = [];
    const rootIndividualActions = [];
    
    // Helper function for calculating remaining trial days
    const calculateRemainingTrialDays = (trialExpiry) => {
        if (!trialExpiry) return 0;
        const expiryDate = new Date(trialExpiry);
        const today = new Date();
        const diffTime = expiryDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    // Helper function to get currency symbol (matching learning plans page)
    const getCurrencySymbol = (currencyCode) => {
        switch (currencyCode) {
            case 'GBP': return '£';
            case 'USD': return '$';
            case 'EUR': return '€';
            default: return currencyCode; // fallback to code if unknown
        }
    };

    // Helper function to calculate plan price (matching learning plans page)
    const calculatePlanPrice = (trialPlanKey, studentCount) => {
        if (!gotSubscriptions?.e141_Pricing) return '';
        
        const byoUnitPrices = {
            students: gotSubscriptions.e141_Pricing.e141_PricePerStudent,
            teacherLogins: gotSubscriptions.e141_Pricing.e141_PricePerTeacherLogin,
            studentLogins: gotSubscriptions.e141_Pricing.e141_PricePerStudentLogin,
            assessments: gotSubscriptions.e141_Pricing.e141_PricePerAssessment,
            lessonsAndActivities: gotSubscriptions.e141_Pricing.e141_PricePerLessonsAndActivities,
            basePriceFamily: gotSubscriptions.e141_Pricing.e141_BasePriceFamily || 0,
            basePriceTutor: gotSubscriptions.e141_Pricing.e141_BasePriceTutor || 0,
        };

        const toMoney = (price) => {
            return Number.isInteger(price) ? price : price.toFixed(2);
        };

        if (trialPlanKey === 'family') {
            // Use provided studentCount if available, otherwise default to 1
            const count = studentCount ?? 1;
            const assessments = count * 30;
            const lessonsAndActivities = count * 100;
            const price = (byoUnitPrices.basePriceFamily || 0) +
                byoUnitPrices.students * count +
                byoUnitPrices.teacherLogins * (2 - 1) + // 2 total, charge for (2-1) = 1
                byoUnitPrices.studentLogins * count +
                byoUnitPrices.assessments * assessments +
                byoUnitPrices.lessonsAndActivities * lessonsAndActivities;
            return toMoney(price);
        } else if (trialPlanKey === 'tutor') {
            // Use provided studentCount if available, otherwise default to 10
            const count = studentCount ?? 10;
            const assessments = count * 10;
            const lessonsAndActivities = count * 30;
            const price = (byoUnitPrices.basePriceTutor || 0) +
                byoUnitPrices.students * count +
                byoUnitPrices.teacherLogins * 0 + // 0 additional (1 included free, fixed)
                byoUnitPrices.assessments * assessments +
                byoUnitPrices.lessonsAndActivities * lessonsAndActivities;
            return toMoney(price);
        }
        
        return '';
    };

    if (requestingUserIsOrgAdmin && profileView.UpdateUrl) {
        rootActions.push(() => <SecondaryActionMenuButton className={css({
            backgroundColor: "white",
            width: '100%'
        })} onClick={() => {
            Paddle.Checkout.open({
                transactionId: profileView.UpdateUrl,
                allowLogout: false
            });
        }}>Update Payment Method</SecondaryActionMenuButton>);
    }

    if (requestingUserIsOrgAdmin && profileView.IsOnPaidPlan) {
        rootActions.push(() => <EditBillingDetails taxMode={profileView.BillingTaxMode}/>);
        rootActions.push(() => <EditBillingCurrency/>);
        rootIndividualActions.push(() => <SecondaryActionMenuButton
            onClick={() => setIsFlipped(true)}
        >View Invoices</SecondaryActionMenuButton>);
    }

    let hiddenFields = ["e138_PlanKey", "e138_AssessmentsLimit", "e138_StartDate", "e138_AssessmentsTakenLimit", "e138_AssessmentsTaken", "e138_StudentsLimit", "e138_TeacherLoginsLimit", "e138_StudentLoginsLimit", "e138_LessonsAndActivitiesLimit", "e138_Questions", "e138_QuestionsLimit", "e138_TrialKey", "e138_TrialExpiry"];
    let hiddenFields2 = ["e138_PlanKey2", "e138_TotalQuestions", "e138_TrialKey", "e138_TrialExpiry", "e138_TrialPaymentDetailsProvided"];

    return (
        <Card className={`relative transition-all duration-500 ${isFlipped ? 'h-auto' : 'min-h-[120px]'}`}>
            <div className={`transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
                {/* Front side - Plan View */}
                <div className={`${isFlipped ? 'hidden' : 'block'} backface-hidden`}>
                    <CardContent className="p-6">
                        {/* Trial Conversion Status Notification */}
                        {profileView.QueuedPlan.e138_TrialKey && profileView.IsOnPaidPlan && (
                            <div className={css({marginBottom: '16px', textAlign: 'center', padding: '12px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '8px'})}>
                                <ParagraphSmall>
                                    You've activated your paid plan! Billing begins when your trial ends on{" "}
                                    {formatDate(profileView.QueuedPlan.e138_TrialExpiry)} ({calculateRemainingTrialDays(profileView.QueuedPlan.e138_TrialExpiry)} days remaining).
                                </ParagraphSmall>
                            </div>
                        )}
                        
                        {/* Trial Days Remaining for Unconverted Trials */}
                        {profileView.QueuedPlan.e138_TrialKey && !profileView.IsOnPaidPlan && (
                            <div className={css({marginBottom: '16px', textAlign: 'center', padding: '12px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '8px'})}>
                                <ParagraphSmall>
                                    {calculateRemainingTrialDays(profileView.QueuedPlan.e138_TrialExpiry)} days remaining in your trial.
                                </ParagraphSmall>
                                {/* Payment details already provided – show confirmation instead of Keep This Plan */}
                                {profileView.QueuedPlan.e138_TrialPaymentDetailsProvided ? (
                                    <>
                                        <ParagraphSmall className={css({marginTop: '8px'})}>
                                            Payment details have been provided. Billing will begin on{" "}
                                            {profileView.QueuedPlan.e138_TrialExpiry
                                                ? formatDate(profileView.QueuedPlan.e138_TrialExpiry)
                                                : 'the end of your trial'}
                                            .
                                        </ParagraphSmall>
                                        <ParagraphSmall className={css({marginTop: '8px'})}>
                                            Visit the{" "}
                                            <a
                                                href="/learning-plans"
                                                style={{textDecoration: 'underline', color: 'inherit'}}
                                            >
                                                Learning Plans
                                            </a>{" "}
                                            page for other options.
                                        </ParagraphSmall>
                                    </>
                                ) : requestingUserIsOrgAdmin ? (
                                    <>
                                        <div className={css({display: 'flex', justifyContent: 'center', marginTop: '8px', marginBottom: '8px'})}>
                                            <PrimaryActionMenuButton 
                                                onClick={() => {
                                                    const trialPlanKey = profileView.QueuedPlan.e138_TrialKey;
                                                    const country = gotSubscriptions?.e141_Pricing?.e141_BillingCountry || window.LYW.PaddleCountryCode;
                                                    const currency = gotSubscriptions?.e141_Pricing?.e141_BillingCurrency || profileView.BillingCurrency || 'GBP';
                                                    
                                                    // Get actual trial student count from profile, with plan-specific fallbacks
                                                    const trialStudentCount = profileView.QueuedPlan.e138_Credit2?.e138_TotalStudents ?? (trialPlanKey === 'family' ? 1 : 10);
                                                    
                                                    const c167 = {
                                                        C167_PlanKey: trialPlanKey,
                                                        C167_Country: country,
                                                        C167_ChosenCurrency: currency
                                                    };
                                                    
                                                    if (trialPlanKey === "family") {
                                                        c167.C167_FamilyStudents = trialStudentCount;
                                                    } else if (trialPlanKey === "tutor") {
                                                        c167.C167_TutorStudents = trialStudentCount;
                                                    }
                                                    
                                                    beginOrderTrigger(appendTrialPromoToBeginOrder(c167));
                                                }}
                                            >
                                                Keep This Plan after the trial ends
                                            </PrimaryActionMenuButton>
                                        </div>
                                        {gotSubscriptions?.e141_Pricing && profileView.QueuedPlan.e138_TrialKey && (() => {
                                            // Get actual trial student count from profile, with plan-specific fallbacks
                                            const trialStudentCount = profileView.QueuedPlan.e138_Credit2?.e138_TotalStudents ?? (profileView.QueuedPlan.e138_TrialKey === 'family' ? 1 : 10);
                                            const price = calculatePlanPrice(profileView.QueuedPlan.e138_TrialKey, trialStudentCount);
                                            return price ? (
                                                <ParagraphSmall className={css({marginTop: '8px'})}>
                                                    <div className={css({display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'})}>
                                                        <span>{getCurrencySymbol(gotSubscriptions.e141_Pricing.e141_BillingCurrency || profileView.BillingCurrency || 'GBP')}{price} /month if you decide to continue.</span>
                                                        <InfoTooltip />
                                                    </div>
                                                </ParagraphSmall>
                                            ) : null;
                                        })()}
                                        <ParagraphSmall className={css({marginTop: '8px'})}>
                                            Visit the{" "}
                                            <a
                                                href="/learning-plans"
                                                style={{textDecoration: 'underline', color: 'inherit'}}
                                            >
                                                Learning Plans
                                            </a>{" "}
                                            page for other options.
                                        </ParagraphSmall>
                                    </>
                                ) : (
                                    <ParagraphSmall className={css({marginTop: '8px'})}>
                                        Speak to your Organisation Admin to change the subscription plan.
                                    </ParagraphSmall>
                                )}
                            </div>
                        )}
                        
                        {/* Starter Access Info Message */}
                        {!profileView.IsOnPaidPlan && !profileView.QueuedPlan.e138_TrialKey && (
                            <div className={css({marginBottom: '16px', textAlign: 'center', padding: '12px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '8px'})}>
                                <ParagraphSmall>
                                    Visit the{" "}
                                    <a 
                                        href="/learning-plans"
                                        style={{textDecoration: 'underline', color: 'inherit'}}
                                    >
                                        Learning Plans
                                    </a>{" "} 
                                    page to see other plan options.
                                </ParagraphSmall>
                            </div>
                        )}
                        
                        {/* Paid Plan Info Message */}
                        {profileView.IsOnPaidPlan && !profileView.QueuedPlan.e138_TrialKey && (
                            <div className={css({marginBottom: '16px', textAlign: 'center', padding: '12px', backgroundColor: theme.colors.backgroundSecondary, borderRadius: '8px'})}>
                                <ParagraphSmall>
                                    Visit the{" "}
                                    <a 
                                        href="/learning-plans"
                                        style={{textDecoration: 'underline', color: 'inherit'}}
                                    >
                                        Learning Plans
                                    </a>{" "} 
                                    page to see other plan options.
                                </ParagraphSmall>
                            </div>
                        )}
                        
                        {/* Student Count Toggle for Family/Tutor Plans - org admin only (financial change) */}
                        {requestingUserIsOrgAdmin && (isFamilyPlan || isTutorPlan) && (
                            <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-1">
                                            {isFamilyPlan ? 'Family Plan' : 'Tutor Plan'} - Student Count
                                        </h3>
                                        <p className="text-xs text-gray-600">
                                            Adjust the number of students in your plan
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Number of Students
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    if (isFamilyPlan) {
                                                        setFamilyStudents(Math.max(1, familyStudents - 1));
                                                    } else {
                                                        setTutorStudents(Math.max(10, tutorStudents - 5));
                                                    }
                                                }}
                                                disabled={(isFamilyPlan && familyStudents <= 1) || (isTutorPlan && tutorStudents <= 10)}
                                                className="h-9 w-9 p-0"
                                            >
                                                −
                                            </Button>
                                            <Input
                                                type="number"
                                                min={isFamilyPlan ? 1 : 10}
                                                max={isFamilyPlan ? 10 : 100}
                                                step={isFamilyPlan ? 1 : 5}
                                                value={isFamilyPlan ? familyStudents : tutorStudents}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10);
                                                    if (!isNaN(val)) {
                                                        if (isFamilyPlan) {
                                                            setFamilyStudents(Math.max(1, Math.min(10, val)));
                                                        } else {
                                                            setTutorStudents(Math.max(10, Math.min(100, val)));
                                                        }
                                                    }
                                                }}
                                                className="w-20 text-center h-9"
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    if (isFamilyPlan) {
                                                        setFamilyStudents(Math.min(10, familyStudents + 1));
                                                    } else {
                                                        setTutorStudents(Math.min(100, tutorStudents + 5));
                                                    }
                                                }}
                                                disabled={(isFamilyPlan && familyStudents >= 10) || (isTutorPlan && tutorStudents >= 100)}
                                                className="h-9 w-9 p-0"
                                            >
                                                +
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                {studentCountDiffers() && (
                                    <div className="mt-4">
                                        <Button
                                            onClick={handleUpgrade}
                                            className="w-full"
                                        >
                                            Change Plan
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <PlanView profileView={profileView} rootActions={rootActions} rootIndividualActions={rootIndividualActions} hiddenFields={hiddenFields} hiddenFields2={hiddenFields2} />
                    </CardContent>
                </div>
                
                {/* Back side - Invoices */}
                <div className={`${isFlipped ? 'block' : 'hidden'} rotate-y-180 backface-hidden bg-white rounded-lg border`}>
                    <CardHeader className="pb-0">
                        <div className="flex items-center justify-between">
                            <CardTitle>Invoices</CardTitle>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => setIsFlipped(false)}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Plan
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <InvoicesCard />
                    </CardContent>
                </div>
            </div>
        </Card>
    );
}

function PlanView({profileView, rootActions, rootIndividualActions, hiddenFields, hiddenFields2}) {
    return (<>
        <AggregateView
            hiddenFields={hiddenFields}
            aggregate={profileView.CurrentPlan} displayMode={false} entityMenus={{
            "root-individual": new EntityMenu(null, rootIndividualActions),
            "root": rootActions.length > 0 ? new EntityMenu(null, rootActions) : undefined,
        }}
            propertyRenderer={{
                "e138_Students": (value, entity) => <Graph used={value} total={entity.e138_StudentsLimit}/>,
                "e138_TeacherLogins": (value, entity) => <Graph used={value} total={entity.e138_TeacherLoginsLimit}/>,
                "e138_StudentLogins": (value, entity) => <Graph used={value} total={entity.e138_StudentLoginsLimit}/>,
                "e138_LessonsAndActivities": (value, entity) => <Graph used={value} total={entity.e138_LessonsAndActivitiesLimit}/>,
                "e138_Assessments": (value, entity) => <Graph used={value} total={entity.e138_AssessmentsLimit}/>,
            }}
            labelRenderer={{
                "e138_TeacherLogins": (value) => <>Teacher Logins</>,
                "e138_StudentLogins": (value) => <>Student Logins</>,
                "e138_LessonsAndActivities": (value) => <>Lessons and Activities</>
            }}
            title={<HeadingSmall>This Month</HeadingSmall>}/>

        <AggregateView
            labelRenderer={{
                "e138_TotalStudents": (value) => <>Students</>,
                "e138_TotalTeacherLogins": (value) => <>Teacher Logins</>,
                "e138_TotalStudentLogins": (value) => <>Student Logins</>,
                "e138_TotalLessonsAndActivities": (value) => <>Lessons and Activities</>,
                "e138_TotalAssessments": (value) => <>Assessments</>
            }}
            
            hiddenFields={hiddenFields2} aggregate={profileView.QueuedPlan} displayMode={false}
            title={<HeadingSmall>Next Month</HeadingSmall>}/>
    </>);
}

function Graph({total, used}) {
    return (
        <ProgressBar
            value={(used + total === 0) ? 0 : ((used / total) * 100)}
            getProgressLabel={value =>
                `${used} out of ${total} used`
            }
            showLabel
        />
    )
}

